import React, { useState, useEffect, useContext } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Grid
} from "@material-ui/core";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  root: { display: "flex", flexWrap: "wrap" },
  btnWrapper: { position: "relative" },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12
  },
  formControl: { margin: theme.spacing(1), minWidth: 120 }
}));

const CATEGORIES = ["api", "plataforma", "app", "tag", "chip", "financeiro", "outro"];
const STATUSES = ["pending", "in_progress", "waiting_customer", "done", "cancelled"];
const PRIORITIES = ["low", "medium", "high", "urgent"];

const CATEGORY_LABELS = {
  api: "API", plataforma: "Plataforma", app: "App", tag: "Tag",
  chip: "Chip", financeiro: "Financeiro", outro: "Outro"
};
const STATUS_LABELS = {
  pending: "Pendente", in_progress: "Em andamento",
  waiting_customer: "Aguardando cliente", done: "Concluído", cancelled: "Cancelado"
};
const PRIORITY_LABELS = {
  low: "Baixa", medium: "Média", high: "Alta", urgent: "Urgente"
};

const NoteSchema = Yup.object().shape({
  title: Yup.string().min(2, "Muito curto").required("Obrigatório"),
  description: Yup.string().required("Obrigatório")
});

const todayISO = () => new Date().toISOString().slice(0, 10);
const toDateInput = (value) => {
  if (!value) return "";
  try {
    return new Date(value).toISOString().slice(0, 10);
  } catch {
    return "";
  }
};

const ImportantClientNoteModal = ({
  open,
  onClose,
  noteId,
  reload,
  defaults = {}
}) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [users, setUsers] = useState([]);

  const initialState = {
    customerName: defaults.customerName || "",
    title: "",
    description: "",
    category: "outro",
    status: "pending",
    priority: "medium",
    requestedAt: todayISO(),
    dueAt: "",
    assignedUserId: "",
    // Vínculos opcionais (preenchidos quando criado a partir do ticket/contato).
    contactId: defaults.contactId || "",
    ticketId: defaults.ticketId || ""
  };

  const [note, setNote] = useState(initialState);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/users", { params: { companyId: user.companyId } });
        setUsers(data.users || []);
      } catch (err) {
        toastError(err);
      }
    })();
  }, [open, user.companyId]);

  useEffect(() => {
    if (!open) return;
    if (!noteId) {
      setNote(initialState);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/important-client-notes/${noteId}`);
        setNote({
          customerName: data.customerName || "",
          title: data.title || "",
          description: data.description || "",
          category: data.category || "outro",
          status: data.status || "pending",
          priority: data.priority || "medium",
          requestedAt: toDateInput(data.requestedAt) || todayISO(),
          dueAt: toDateInput(data.dueAt),
          assignedUserId: data.assignedUserId || "",
          contactId: data.contactId || "",
          ticketId: data.ticketId || ""
        });
      } catch (err) {
        toastError(err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteId, open]);

  const handleClose = () => {
    setNote(initialState);
    onClose();
  };

  const handleSave = async (values) => {
    const payload = {
      ...values,
      assignedUserId: values.assignedUserId || null,
      dueAt: values.dueAt || null,
      contactId: values.contactId || null,
      ticketId: values.ticketId || null
    };
    try {
      if (noteId) {
        await api.put(`/important-client-notes/${noteId}`, payload);
      } else {
        await api.post("/important-client-notes", payload);
      }
      toast.success(i18n.t("importantClientNotes.toasts.success"));
      if (typeof reload === "function") reload();
    } catch (err) {
      toastError(err);
    }
    handleClose();
  };

  return (
    <div className={classes.root}>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle>
          {noteId
            ? i18n.t("importantClientNotes.dialog.edit")
            : i18n.t("importantClientNotes.dialog.add")}
        </DialogTitle>
        <Formik
          initialValues={note}
          enableReinitialize
          validationSchema={NoteSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSave(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting }) => (
            <Form>
              <DialogContent dividers>
                <Grid container spacing={1}>
                  <Grid item xs={12} md={6}>
                    <Field
                      as={TextField}
                      autoFocus
                      label={i18n.t("importantClientNotes.dialog.customerName")}
                      name="customerName"
                      placeholder={i18n.t("importantClientNotes.placeholders.customerName")}
                      helperText={i18n.t("importantClientNotes.placeholders.customerNameHint")}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Field
                      as={TextField}
                      label={i18n.t("importantClientNotes.dialog.title")}
                      name="title"
                      placeholder={i18n.t("importantClientNotes.placeholders.title")}
                      error={touched.title && Boolean(errors.title)}
                      helperText={touched.title && errors.title}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      label={i18n.t("importantClientNotes.dialog.description")}
                      name="description"
                      placeholder={i18n.t("importantClientNotes.placeholders.description")}
                      multiline
                      rows={6}
                      error={touched.description && Boolean(errors.description)}
                      helperText={touched.description && errors.description}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <FormControl variant="outlined" margin="dense" fullWidth>
                      <InputLabel>{i18n.t("importantClientNotes.dialog.category")}</InputLabel>
                      <Field as={Select} name="category" label={i18n.t("importantClientNotes.dialog.category")}>
                        {CATEGORIES.map((c) => (
                          <MenuItem key={c} value={c}>{CATEGORY_LABELS[c]}</MenuItem>
                        ))}
                      </Field>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl variant="outlined" margin="dense" fullWidth>
                      <InputLabel>{i18n.t("importantClientNotes.dialog.priority")}</InputLabel>
                      <Field as={Select} name="priority" label={i18n.t("importantClientNotes.dialog.priority")}>
                        {PRIORITIES.map((p) => (
                          <MenuItem key={p} value={p}>{PRIORITY_LABELS[p]}</MenuItem>
                        ))}
                      </Field>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl variant="outlined" margin="dense" fullWidth>
                      <InputLabel>{i18n.t("importantClientNotes.dialog.status")}</InputLabel>
                      <Field as={Select} name="status" label={i18n.t("importantClientNotes.dialog.status")}>
                        {STATUSES.map((s) => (
                          <MenuItem key={s} value={s}>{STATUS_LABELS[s]}</MenuItem>
                        ))}
                      </Field>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Field
                      as={TextField}
                      type="date"
                      label={i18n.t("importantClientNotes.dialog.requestedAt")}
                      name="requestedAt"
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Field
                      as={TextField}
                      type="date"
                      label={i18n.t("importantClientNotes.dialog.dueAt")}
                      name="dueAt"
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl variant="outlined" margin="dense" fullWidth>
                      <InputLabel>{i18n.t("importantClientNotes.dialog.assigned")}</InputLabel>
                      <Field as={Select} name="assignedUserId" label={i18n.t("importantClientNotes.dialog.assigned")}>
                        <MenuItem value="">&nbsp;</MenuItem>
                        {users.map((u) => (
                          <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                        ))}
                      </Field>
                    </FormControl>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleClose} color="secondary" disabled={isSubmitting} variant="outlined">
                  {i18n.t("importantClientNotes.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {noteId
                    ? i18n.t("importantClientNotes.buttons.save")
                    : i18n.t("importantClientNotes.buttons.add")}
                  {isSubmitting && <CircularProgress size={24} className={classes.buttonProgress} />}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default ImportantClientNoteModal;
